'use client';

import {
  CONSENT_DECISION_PENDING_KEY,
  CONSENT_LEVELS,
  type ConsentLevel,
  useConsentManagement,
} from '@/hooks/useConsentManagement';
import { clearRDStationBannerChoice, grantRDStationConsent } from '@/utils/rdStationConsent';
import Script from 'next/script';
import { useEffect } from 'react';
import FacebookPixel from './FacebookPixel';

const GTM_ID = 'GTM-MRWWPBNV';
const GA_ID = 'G-S829NHLM4Z';
const RD_LOADER_SRC =
  'https://d335luupugsy2.cloudfront.net/js/loader-scripts/01a1f931-3a2d-4e8e-ba7f-f67424f05612-loader.js';

const isProduction = process.env.NEXT_PUBLIC_APP_ENV === 'production';

function buildConsentState(level: ConsentLevel) {
  const value = level === 'full' ? 'granted' : 'denied';
  return {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
    functionality_storage: 'granted',
    security_storage: 'granted',
  };
}

export default function TrackingTags() {
  const { consent } = useConsentManagement();

  // The cookie is client-controlled and is interpolated into an inline script: only whitelisted values pass.
  const level: ConsentLevel = consent && CONSENT_LEVELS.includes(consent.level) ? consent.level : 'none';

  useEffect(() => {
    if (!isProduction || level === 'full') return;
    clearRDStationBannerChoice();
  }, [level]);

  if (!isProduction || !consent) return null;

  return (
    <>
      <Script
        id='tracking-bootstrap'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('consent', 'default', ${JSON.stringify(buildConsentState(level))});
            try {
              if (sessionStorage.getItem(${JSON.stringify(CONSENT_DECISION_PENDING_KEY)})) {
                sessionStorage.removeItem(${JSON.stringify(CONSENT_DECISION_PENDING_KEY)});
                dataLayer.push({ event: 'cookie_consent_update', consent_level: ${JSON.stringify(level)} });
              }
            } catch (e) {}
            gtag('js', new Date());
            gtag('config', '${GA_ID}');
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTM_ID}');
          `,
        }}
      />
      <Script
        id='google-analytics'
        strategy='lazyOnload'
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
      />
      {level === 'full' && (
        <>
          <FacebookPixel />
          <Script id='rdstation' strategy='lazyOnload' src={RD_LOADER_SRC} onLoad={() => grantRDStationConsent()} />
        </>
      )}
    </>
  );
}
